import type { MechanicDefinition } from "./types";

export const cardGame: MechanicDefinition = {
  id: "card_game",
  disabled: true,
  name: "Card System",
  description: "Collectible card mechanics with hand management, draws, and card-based abilities",
  icon: "RectangleVertical",
  category: "systems",
  aiHints: {
    purpose: "Adds a card hand mechanic — TCG combat cards, tarot fate cards, deck-building, or resource hand management.",
    whenToUse: "Worlds wanting card-based gameplay layered on top of narrative — card battlers, fortune-telling, deck-builders.",
    synergies: ["combat", "economy", "character_classes"],
    conflicts: [],
    prerequisites: [],
    tags: ["cards", "tcg", "tarot", "deck-builder", "hand-management", "collectible"],
    complexity: 3,
    exampleConfig: { style: "tcg", hand_size: 5, draw_per_turn: 1 },
    genrePresets: {
      "card_battler": { style: "tcg", hand_size: 7, draw_per_turn: 2 },
      "fortune_telling": { style: "tarot", hand_size: 3, draw_per_turn: 1 },
      "roguelike": { style: "deck_builder", hand_size: 5, draw_per_turn: 1 },
    },
    stateVariables: ["player_hand", "deck_size", "discard_count"],
  },
  configSchema: [
    {
      key: "style",
      label: "Card Style",
      type: "select",
      options: [
        { value: "tcg", label: "Trading Card Game (battle cards)" },
        { value: "tarot", label: "Tarot / Fortune (fate cards)" },
        { value: "deck_builder", label: "Deck Builder (acquire & improve)" },
        { value: "hand_management", label: "Hand Management (resource cards)" },
      ],
      default: "tcg",
      description: "What kind of card system to use",
    },
    {
      key: "hand_size",
      label: "Max Hand Size",
      type: "number",
      default: 5,
      description: "Maximum cards the player can hold",
    },
    {
      key: "draw_per_turn",
      label: "Cards Drawn Per Turn",
      type: "number",
      default: 1,
      description: "How many cards are drawn each turn",
    },
  ],
  systemPromptSection: (config) => {
    const style = config.style || "tcg";
    const handSize = config.hand_size || 5;
    const drawPerTurn = config.draw_per_turn || 1;
    const styleDesc: Record<string, string> = {
      tcg: "Cards represent abilities, creatures, or spells. Players draw cards and play them in combat or encounters. Each card has a name, type, rarity, cost, and effect description.",
      tarot: "Cards represent fate and fortune. Drawing cards reveals narrative paths, blessings, or curses. The narrator draws cards at dramatic moments to influence the story.",
      deck_builder: "Players start with basic cards and acquire powerful ones through exploration and victories. Cards improve over time. Deck composition matters.",
      hand_management: "Cards represent resources, skills, or tools. Players manage a hand of options, choosing which to play each turn. Spent cards may return to the deck or be discarded permanently.",
    };
    return `## CARD SYSTEM (Hard Mechanic)
- Style: ${style}
- Max hand size: ${handSize} cards
- Draw ${drawPerTurn} card(s) per turn
- ${styleDesc[style]}
- Track the player's hand in state variables as "player_hand" (array of card objects with: name, type, rarity, cost, effect, image_url).
- Track the deck as "deck_size" count in variables.
- Track the discard pile as "discard_count" in variables.
- Use draw_cards tool to add cards to hand. Use play_card to use a card.
- IMPORTANT: For each card in draw_cards, provide an "image_prompt" describing vivid visual art for the card. No text, no UI — just the subject in a painterly style.
- Cards can represent ANY entity: items, NPCs, creatures, locations, spells, or abstract concepts.
- When a card is played, describe its effect narratively and apply consequences.
- Cards can be found as loot, rewards, purchased, or drawn from fate.
- Each card should feel unique with flavor text appropriate to the world.
- If a card represents an existing world entity, use that entity's description for the image_prompt.`;
  },
  widgets: [
    {
      id: "cards_tab",
      type: "sidebar_tab",
      label: "Cards",
      icon: "RectangleVertical",
      component: "CardHand",
      dataBindings: {
        hand: "variables.player_hand",
        deckSize: "variables.deck_size",
        discardCount: "variables.discard_count",
      },
      visibility: "when_active",
      priority: 50,
    },
  ],
  tools: [
    {
      type: "function",
      function: {
        name: "draw_cards",
        description: "Draw cards from the deck into the player's hand. Creates new cards appropriate to the situation.",
        parameters: {
          type: "object",
          properties: {
            count: { type: "number", description: "Number of cards to draw" },
            cards: {
              type: "array",
              description: "The cards being drawn (narrator decides what they are)",
              items: {
                type: "object",
                properties: {
                  name: { type: "string" },
                  type: { type: "string", description: "Card type: attack, defense, spell, item, creature, fate, etc." },
                  rarity: { type: "string", enum: ["common", "uncommon", "rare", "epic", "legendary"] },
                  cost: { type: "number", description: "Resource cost to play" },
                  effect: { type: "string", description: "What the card does when played" },
                  image_prompt: { type: "string", description: "Visual description for AI card art. Describe the subject vividly — creature, spell effect, item, etc. No text or UI elements." },
                },
                required: ["name", "type", "effect", "image_prompt"],
              },
            },
            reason: { type: "string", description: "Why cards are being drawn" },
          },
          required: ["count", "cards", "reason"],
        },
      },
    },
    {
      type: "function",
      function: {
        name: "play_card",
        description: "Play a card from the player's hand, applying its effect",
        parameters: {
          type: "object",
          properties: {
            card_name: { type: "string", description: "Name of the card being played" },
            target: { type: "string", description: "Target of the card effect (if applicable)" },
            result: { type: "string", description: "Narrative result of playing the card" },
          },
          required: ["card_name", "result"],
        },
      },
    },
  ],
};
